import { useState, useEffect, useMemo, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDayEntries, useUpdateEntry } from "../hooks/useApi";
import { apiClient } from "../api/client";
import {
  Entry,
  formatQuantity,
  getVarianceMeta,
  isCounted,
  localISODate,
  toNumber,
} from "../lib/entries";
import { DraftNumberInput } from "../components/DraftNumberInput";
import { AdjustmentsDisclosure } from "../components/AdjustmentsDisclosure";
import { SaveStatusChip, SaveStatus } from "../components/SaveStatusChip";
import { StoreCountMode } from "../components/StoreCountMode";

type Filter = "all" | "uncounted" | "variance";

interface FailedSave {
  entryId: string;
  field: string;
  raw: string;
}

export function StoreCount({ storeId }: { storeId: string }) {
  const [date, setDate] = useState(() => localISODate());
  const { data: entries, isLoading } = useDayEntries(storeId, date);
  const updateEntry = useUpdateEntry();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [countModeOpen, setCountModeOpen] = useState(false);
  const [lockedMessage, setLockedMessage] = useState<string | null>(null);

  const [pendingSaves, setPendingSaves] = useState(0);
  const [failedSaves, setFailedSaves] = useState<Map<string, FailedSave>>(
    new Map()
  );
  const [justSaved, setJustSaved] = useState(false);
  const savedTimerRef = useRef<number | null>(null);

  // Spec §9: opening the day is automatic on load. open_day is idempotent,
  // so calling it on every date change is safe.
  useEffect(() => {
    let cancelled = false;
    setLockedMessage(null);
    apiClient
      .openDay(storeId, date)
      .then(() => {
        if (!cancelled) {
          queryClient.invalidateQueries({ queryKey: ["entries", storeId, date] });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 409) {
          setLockedMessage(
            err.response?.data?.error === "Period is locked"
              ? "This month has been locked by an admin. Entries are read-only."
              : err.response?.data?.error || "This period is locked."
          );
        } else {
          console.error("Failed to open day:", err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [storeId, date, queryClient]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current !== null)
        window.clearTimeout(savedTimerRef.current);
    };
  }, []);

  const runSave = async (entryId: string, field: string, raw: string) => {
    const trimmed = raw.trim();
    const value =
      trimmed === "" ? (field === "physicalCount" ? null : 0) : parseFloat(trimmed);
    if (value !== null && Number.isNaN(value)) return;
    const key = `${entryId}:${field}`;

    setPendingSaves((n) => n + 1);
    try {
      await updateEntry.mutateAsync({ entryId, data: { [field]: value } });
      setFailedSaves((prev) => {
        if (!prev.has(key)) return prev;
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      setJustSaved(true);
      if (savedTimerRef.current !== null)
        window.clearTimeout(savedTimerRef.current);
      savedTimerRef.current = window.setTimeout(() => setJustSaved(false), 2000);
    } catch (error: any) {
      if (error?.response?.status === 409) {
        setLockedMessage(
          "This month has been locked by an admin. Entries are read-only."
        );
      } else {
        setFailedSaves((prev) => {
          const next = new Map(prev);
          next.set(key, { entryId, field, raw });
          return next;
        });
      }
      console.error("Failed to update entry:", error);
    } finally {
      setPendingSaves((n) => n - 1);
    }
  };

  const handleSaveField = (entryId: string, field: string, raw: string) => {
    void runSave(entryId, field, raw);
  };

  const retryFailedSaves = () => {
    const toRetry = Array.from(failedSaves.values());
    setFailedSaves(new Map());
    toRetry.forEach(({ entryId, field, raw }) => {
      void runSave(entryId, field, raw);
    });
  };

  const dailyItems = useMemo(
    () => ((entries || []) as Entry[]).filter((e) => e.section === "DAILY"),
    [entries]
  );

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    return dailyItems.filter((entry) => {
      if (
        term &&
        !entry.name.toLowerCase().includes(term) &&
        !(entry.code ?? "").toLowerCase().includes(term)
      ) {
        return false;
      }
      if (filter === "uncounted") return !isCounted(entry);
      if (filter === "variance") {
        const variance = toNumber(entry.variance);
        return variance !== null && variance !== 0;
      }
      return true;
    });
  }, [dailyItems, search, filter]);

  const countedItems = dailyItems.filter(isCounted).length;
  const uncountedCount = dailyItems.length - countedItems;
  const completionPercent = dailyItems.length
    ? Math.round((countedItems / dailyItems.length) * 100)
    : 0;
  const netVariance = dailyItems.reduce(
    (sum, entry) => sum + (toNumber(entry.variance) ?? 0),
    0
  );

  const locked = lockedMessage !== null;

  const saveStatus: SaveStatus = {
    pending: pendingSaves,
    failedCount: failedSaves.size,
    justSaved,
  };

  const jumpToNextUncounted = () => {
    const target = visibleItems.find((entry) => !isCounted(entry));
    const fallback = dailyItems.find((entry) => !isCounted(entry));
    const entry = target ?? fallback;
    if (!entry) return;
    document
      .getElementById(`entry-card-${entry.id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  if (isLoading) {
    return (
      <div className="page-shell">
        <p className="state-note">Loading daily entries…</p>
      </div>
    );
  }

  if (countModeOpen) {
    return (
      <StoreCountMode
        entries={dailyItems}
        onSaveField={handleSaveField}
        onClose={() => setCountModeOpen(false)}
        locked={locked}
        saveStatus={saveStatus}
        onRetryFailedSaves={retryFailedSaves}
      />
    );
  }

  return (
    <div className="page-shell has-bottom-bar">
      <header className="page-header">
        <div>
          <h1>Daily stock count</h1>
          <p>Update purchases, sales, and physical counts quickly from the floor.</p>
        </div>
        <div className="page-header-actions">
          <label className="field-inline">
            <span>Date</span>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input-control"
            />
          </label>
          <button
            type="button"
            className="primary-action"
            onClick={() => setCountModeOpen(true)}
            disabled={dailyItems.length === 0}
          >
            {uncountedCount > 0
              ? `Start counting (${uncountedCount} left)`
              : "Review count"}
          </button>
        </div>
      </header>

      {lockedMessage && (
        <div className="locked-banner" role="alert">
          {lockedMessage}
        </div>
      )}

      <section className="stats-grid">
        <article className="stat-card">
          <p className="stat-label">Items</p>
          <p className="stat-value">{dailyItems.length}</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Counted</p>
          <p className="stat-value">
            {countedItems}/{dailyItems.length || 0}
          </p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Completion</p>
          <p className="stat-value">{completionPercent}%</p>
        </article>
        <article className="stat-card">
          <p className="stat-label">Net variance</p>
          <p
            className={`stat-value ${
              netVariance < 0
                ? "metric-negative"
                : netVariance > 0
                  ? "metric-positive"
                  : "metric-neutral"
            }`}
          >
            {netVariance.toFixed(2)}
          </p>
        </article>
      </section>

      <div className="list-toolbar">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search item or code…"
          className="input-control list-search"
        />
        <div className="filter-chips">
          <FilterChip
            label="All"
            active={filter === "all"}
            onClick={() => setFilter("all")}
          />
          <FilterChip
            label={`Uncounted (${uncountedCount})`}
            active={filter === "uncounted"}
            onClick={() => setFilter("uncounted")}
          />
          <FilterChip
            label="With variance"
            active={filter === "variance"}
            onClick={() => setFilter("variance")}
          />
        </div>
        <div className="list-toolbar-status">
          <SaveStatusChip status={saveStatus} onRetry={retryFailedSaves} />
        </div>
      </div>

      {visibleItems.length === 0 ? (
        <p className="state-note">
          {dailyItems.length === 0
            ? "No daily items for this date."
            : "No items match the current search or filter."}
        </p>
      ) : (
        <>
          <div className="inventory-table-wrap desktop-only">
            <table className="inventory-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="table-number">Opening</th>
                  <th>Purchases</th>
                  <th>Sales</th>
                  <th className="table-number">Closing</th>
                  <th>Physical count</th>
                  <th className="table-number">Variance</th>
                  <th>Adjustments</th>
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((entry) => {
                  const variance = toNumber(entry.variance);
                  const varianceMeta = getVarianceMeta(entry.variance);
                  return (
                    <tr key={entry.id}>
                      <td>
                        <p className="table-item-title">{entry.name}</p>
                        {entry.code && (
                          <p className="table-item-subtitle">{entry.code}</p>
                        )}
                      </td>
                      <td className="table-number">
                        {formatQuantity(entry.opening)}
                      </td>
                      <td>
                        <DraftNumberInput
                          value={entry.purchases}
                          onSave={(raw) =>
                            handleSaveField(entry.id, "purchases", raw)
                          }
                          className="numeric-input compact"
                          disabled={locked}
                        />
                      </td>
                      <td>
                        <DraftNumberInput
                          value={entry.sales}
                          onSave={(raw) => handleSaveField(entry.id, "sales", raw)}
                          className="numeric-input compact"
                          disabled={locked}
                        />
                      </td>
                      <td className="table-number">
                        {formatQuantity(entry.closing)}
                      </td>
                      <td>
                        <DraftNumberInput
                          value={entry.physicalCount}
                          onSave={(raw) =>
                            handleSaveField(entry.id, "physicalCount", raw)
                          }
                          placeholder="Enter count"
                          className="numeric-input compact"
                          disabled={locked}
                        />
                      </td>
                      <td className="table-number">
                        <span className={`badge badge-${varianceMeta.tone}`}>
                          {variance === null ? "—" : variance.toFixed(2)}
                        </span>
                      </td>
                      <td className="table-adjustments">
                        <AdjustmentsDisclosure
                          entry={entry}
                          onSaveField={handleSaveField}
                          disabled={locked}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="entry-card-list mobile-only">
            {visibleItems.map((entry) => {
              const variance = toNumber(entry.variance);
              const varianceMeta = getVarianceMeta(entry.variance);
              return (
                <article
                  key={entry.id}
                  id={`entry-card-${entry.id}`}
                  className={`entry-card ${isCounted(entry) ? "is-counted" : ""}`}
                >
                  <div className="entry-card-head">
                    <div>
                      <p className="table-item-title">{entry.name}</p>
                      <p className="table-item-subtitle">
                        {entry.code ?? "No code"}
                      </p>
                    </div>
                    <span className={`badge badge-${varianceMeta.tone}`}>
                      {varianceMeta.label}
                    </span>
                  </div>

                  <div className="entry-metrics">
                    <div className="metric-chip">
                      <span>Opening</span>
                      <strong>{formatQuantity(entry.opening)}</strong>
                    </div>
                    <div className="metric-chip">
                      <span>Closing</span>
                      <strong>{formatQuantity(entry.closing)}</strong>
                    </div>
                    <div className="metric-chip">
                      <span>Variance</span>
                      <strong
                        className={
                          variance === null
                            ? "metric-neutral"
                            : variance < 0
                              ? "metric-negative"
                              : variance > 0
                                ? "metric-positive"
                                : "metric-neutral"
                        }
                      >
                        {variance === null ? "—" : variance.toFixed(2)}
                      </strong>
                    </div>
                  </div>

                  <label className="entry-physical">
                    <span>Physical count</span>
                    <DraftNumberInput
                      value={entry.physicalCount}
                      onSave={(raw) =>
                        handleSaveField(entry.id, "physicalCount", raw)
                      }
                      placeholder="Enter count"
                      className="numeric-input entry-physical-input"
                      disabled={locked}
                    />
                  </label>

                  <div className="entry-secondary-grid">
                    <label>
                      <span>Purchases</span>
                      <DraftNumberInput
                        value={entry.purchases}
                        onSave={(raw) =>
                          handleSaveField(entry.id, "purchases", raw)
                        }
                        className="numeric-input compact"
                        disabled={locked}
                      />
                    </label>
                    <label>
                      <span>Sales</span>
                      <DraftNumberInput
                        value={entry.sales}
                        onSave={(raw) => handleSaveField(entry.id, "sales", raw)}
                        className="numeric-input compact"
                        disabled={locked}
                      />
                    </label>
                  </div>

                  <AdjustmentsDisclosure
                    entry={entry}
                    onSaveField={handleSaveField}
                    disabled={locked}
                  />
                </article>
              );
            })}
          </div>
        </>
      )}

      <div className="bottom-bar mobile-only">
        <div className="bottom-bar-progress">
          <div className="progress-shell">
            <span
              className={`progress-fill ${
                completionPercent === 100
                  ? "is-complete"
                  : completionPercent >= 50
                    ? "is-mid"
                    : "is-low"
              }`}
              style={{ width: `${completionPercent}%` }}
            />
          </div>
          <span className="bottom-bar-label">
            {countedItems}/{dailyItems.length} counted
          </span>
        </div>
        {uncountedCount > 0 ? (
          <button
            type="button"
            className="bottom-bar-action"
            onClick={jumpToNextUncounted}
          >
            Next uncounted
          </button>
        ) : (
          <span className="badge badge-success">All counted</span>
        )}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`filter-chip ${active ? "is-active" : ""}`}
      onClick={onClick}
    >
      {label}
    </button>
  );
}
